export interface PatientNameParts {
  firstName: string;
  middleName: string;
  lastName: string;
}

/**
 * Best-effort split of a single "Full Name" text field into the
 * first / middle / last parts that the patient API and the rest of the app
 * still rely on.
 *
 * - "John Michael Doe" -> { firstName: 'John', middleName: 'Michael', lastName: 'Doe' }
 * - "John Doe"         -> { firstName: 'John', middleName: '', lastName: 'Doe' }
 * - "Madonna"          -> { firstName: 'Madonna', middleName: '', lastName: 'Madonna' }
 *
 * The patient API requires both the first and last name to be non-empty, so a
 * single-word name is stored in both columns and collapsed back to one word by
 * {@link joinFullName}.
 */
export function splitFullName(fullName: string): PatientNameParts {
  const parts = (fullName || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) {
    return { firstName: '', middleName: '', lastName: '' };
  }
  if (parts.length === 1) {
    return { firstName: parts[0], middleName: '', lastName: parts[0] };
  }
  return {
    firstName: parts[0],
    middleName: parts.slice(1, -1).join(' '),
    lastName: parts[parts.length - 1],
  };
}

/**
 * Rebuild the "Full Name" string from the stored name parts. A single-word name
 * is saved into both the first and last name columns, so an identical first and
 * last name with no middle name is collapsed back into one word.
 */
export function joinFullName(parts: {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}): string {
  const first = (parts.firstName || '').trim();
  const middle = (parts.middleName || '').trim();
  const last = (parts.lastName || '').trim();
  if (!middle && first && last && first.toLowerCase() === last.toLowerCase()) {
    return first;
  }
  return [first, middle, last].filter(Boolean).join(' ');
}

/**
 * Build the two-letter avatar initials for a patient. Unlike a naive
 * `${firstName[0]}${lastName[0]}` this collapses a one-word name (stored in both
 * name columns) to a single letter, so "Madonna" renders as "M" rather than
 * "MM". Returns an empty string when no name is available so callers can fall
 * back to another identifier (e.g. the registration number).
 */
export function patientInitials(parts: {
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
}): string {
  const words = joinFullName(parts).split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return `${words[0].charAt(0)}${words[words.length - 1].charAt(0)}`.toUpperCase();
}
